import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/use-session";
import { usePrefs } from "@/lib/prefs-store";
import type { UnitsPreference } from "@/lib/prefs-store";
import { formatError } from "@/lib/format-error";
import { SkeletonLoader } from "@/components/system";

/**
 * SPEC + spec/bootstrap/README.md "Setup wizard" — first-launch UX.
 *
 * Three steps:
 *   1. Identity   → display_name, sex_at_birth, dob, height_cm, current_weight_kg,
 *                   goal_weight_kg, units_preference, language
 *   2. Context    → diet, kitchen, activity_mode, cooking_for, location.country/city/tz
 *   3. Confirm    → recap + button to write rows + call bootstrap_user_seeds()
 *
 * Writes:
 *   - one row in athlete_profile (singular per user)
 *   - one row in context (effective_from = now, effective_to = null)
 *   - then supabase.rpc("bootstrap_user_seeds") to populate Sport + Paper + Evidence
 *
 * Skips integration connection (deferred until Build Week 4).
 */

type Identity = {
  display_name: string;
  sex_at_birth: "male" | "female" | "intersex" | "";
  date_of_birth: string;
  height_cm: string;
  current_weight_kg: string;
  goal_weight_kg: string;
  units_preference: UnitsPreference;
  language: string;
};

type ContextValues = {
  diet: "vegan" | "vegetarian" | "pescatarian" | "omnivore" | "custom";
  kitchen: "full" | "boil_only" | "thaw_only" | "camp_stove" | "restaurant_only" | "no_kitchen";
  activity_mode: string;
  cooking_for: "self" | "self_plus_partner" | "self_plus_guests" | "guests_only";
  country: string;
  city: string;
  time_zone: string;
};

const DEFAULT_IDENTITY: Identity = {
  display_name: "",
  sex_at_birth: "",
  date_of_birth: "",
  height_cm: "",
  current_weight_kg: "",
  goal_weight_kg: "",
  units_preference: "metric_with_imperial_secondary",
  language: "en",
};

const DEFAULT_CONTEXT: ContextValues = {
  diet: "vegan",
  kitchen: "full",
  activity_mode: "home",
  cooking_for: "self",
  country: "CH",
  city: "Zürich",
  time_zone:
    typeof Intl !== "undefined"
      ? Intl.DateTimeFormat().resolvedOptions().timeZone
      : "Europe/Zurich",
};

export function Onboarding() {
  const { session } = useSession();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const setUnits = usePrefs((s) => s.setUnits);

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [identity, setIdentity] = useState<Identity>(DEFAULT_IDENTITY);
  const [ctx, setCtx] = useState<ContextValues>(DEFAULT_CONTEXT);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(false);

  const userId = session?.user?.id ?? null;
  const userEmail = session?.user?.email ?? "";

  // Read existing profile + active context so the wizard hydrates with the
  // user's stored values when they arrive via bookmark / manual nav after
  // setup. Without this the form would silently overwrite real data with
  // DEFAULT_* on submit.
  const existing = useQuery({
    queryKey: ["onboarding_existing", userId],
    enabled: Boolean(userId),
    staleTime: 30_000,
    queryFn: async () => {
      if (!userId) return null;
      const [profileRes, contextRes] = await Promise.all([
        supabase
          .from("athlete_profile")
          .select("*")
          .eq("user_id", userId)
          .maybeSingle(),
        supabase
          .from("context")
          .select("*")
          .eq("user_id", userId)
          .is("effective_to", null)
          .order("effective_from", { ascending: false })
          .limit(1)
          .maybeSingle(),
      ]);
      if (profileRes.error) throw profileRes.error;
      if (contextRes.error) throw contextRes.error;
      return { profile: profileRes.data, context: contextRes.data };
    },
  });

  const hasExisting = Boolean(existing.data?.profile);

  // We need to seed several pieces of form state from the existing profile +
  // context once the query resolves. `useEffect` + `setState` is the right
  // shape here (we sync external server state into local form state exactly
  // once via the `hydrated` guard), but the new react-hooks/set-state-in-effect
  // rule flags it. Disable for this specific block.
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (hydrated) return;
    if (!existing.data) return;
    const p = existing.data.profile;
    const c = existing.data.context as
      | {
          diet?: string;
          kitchen?: string;
          activity_mode?: string;
          cooking_for?: string;
          location?: {
            country?: string;
            city?: string;
            time_zone?: string;
          };
        }
      | null;
    if (p) {
      setIdentity({
        display_name: p.display_name ?? "",
        sex_at_birth:
          (p.sex_at_birth as Identity["sex_at_birth"]) ?? "",
        date_of_birth: p.date_of_birth ?? "",
        height_cm: p.height_cm != null ? String(p.height_cm) : "",
        current_weight_kg:
          p.current_weight_kg != null ? String(p.current_weight_kg) : "",
        goal_weight_kg:
          p.goal_weight_kg != null ? String(p.goal_weight_kg) : "",
        units_preference:
          (p.units_preference as UnitsPreference) ??
          "metric_with_imperial_secondary",
        language: p.language ?? "en",
      });
    }
    if (c) {
      setCtx({
        diet: (c.diet as ContextValues["diet"]) ?? DEFAULT_CONTEXT.diet,
        kitchen:
          (c.kitchen as ContextValues["kitchen"]) ?? DEFAULT_CONTEXT.kitchen,
        activity_mode: c.activity_mode ?? DEFAULT_CONTEXT.activity_mode,
        cooking_for:
          (c.cooking_for as ContextValues["cooking_for"]) ??
          DEFAULT_CONTEXT.cooking_for,
        country: c.location?.country ?? DEFAULT_CONTEXT.country,
        city: c.location?.city ?? DEFAULT_CONTEXT.city,
        time_zone: c.location?.time_zone ?? DEFAULT_CONTEXT.time_zone,
      });
    }
    setHydrated(true);
  }, [existing.data, hydrated]);
  /* eslint-enable react-hooks/set-state-in-effect */

  if (!userId) {
    // AuthGate should never let us get here without a session, but guard.
    return null;
  }

  if (existing.isLoading || (existing.data && !hydrated)) {
    return (
      <Centered>
        <SkeletonLoader variant="card" width={320} height={160} />
      </Centered>
    );
  }

  async function submit() {
    if (!userId) return;
    setSubmitting(true);
    setError(null);
    try {
      const heightCm = identity.height_cm ? Number(identity.height_cm) : null;
      const currentWeightKg = identity.current_weight_kg
        ? Number(identity.current_weight_kg)
        : null;
      const goalWeightKg = identity.goal_weight_kg
        ? Number(identity.goal_weight_kg)
        : null;

      // athlete_profile has a unique(user_id) constraint so a single upsert
      // covers both first-time setup and post-hoc edits.
      const { error: profileErr } = await supabase
        .from("athlete_profile")
        .upsert(
          {
            user_id: userId,
            display_name: identity.display_name.trim() || "Jonas",
            sex_at_birth: identity.sex_at_birth || null,
            date_of_birth: identity.date_of_birth || null,
            height_cm: heightCm,
            current_weight_kg: currentWeightKg,
            goal_weight_kg: goalWeightKg,
            units_preference: identity.units_preference,
            language: identity.language || "en",
            timezone: ctx.time_zone,
          },
          { onConflict: "user_id" },
        );
      if (profileErr) throw profileErr;

      // context is versioned: keep the active row updated in place (rather
      // than spawning a fresh one) when the user is editing existing setup,
      // otherwise insert a brand-new active row.
      const existingContextId = existing.data?.context?.id ?? null;
      const contextPayload = {
        user_id: userId,
        effective_from: existingContextId
          ? existing.data?.context?.effective_from ?? new Date().toISOString()
          : new Date().toISOString(),
        effective_to: null,
        diet: ctx.diet,
        diet_custom_rules: [],
        kitchen: ctx.kitchen,
        location: {
          gps: null,
          country: ctx.country,
          city: ctx.city,
          time_zone: ctx.time_zone,
          derived_at: new Date().toISOString(),
          source: "manual",
        },
        activity_mode: ctx.activity_mode,
        cooking_for: ctx.cooking_for,
        notes: "",
      };

      if (existingContextId) {
        const { error: contextErr } = await supabase
          .from("context")
          .update(contextPayload)
          .eq("id", existingContextId);
        if (contextErr) throw contextErr;
      } else {
        const { error: contextErr } = await supabase
          .from("context")
          .insert(contextPayload);
        if (contextErr) throw contextErr;
      }

      // Best-effort seed loaders. Each fires independently; failure of one
      // doesn't block the others or onboarding. User can re-run from
      // Settings → Reload seeds later. Skipped on edits — seeds are
      // idempotent but unnecessary work the second time around.
      if (!hasExisting) {
        const [
          { error: knowledgeErr },
          { error: projectsErr },
          { error: supplementErr },
        ] = await Promise.all([
          supabase.rpc("bootstrap_user_seeds"),
          supabase.rpc("bootstrap_projects_seeds"),
          supabase.rpc("bootstrap_supplement_seeds"),
        ]);
        if (knowledgeErr) {
          // eslint-disable-next-line no-console
          console.warn("[onboarding] bootstrap_user_seeds failed:", knowledgeErr);
        }
        if (projectsErr) {
          // eslint-disable-next-line no-console
          console.warn(
            "[onboarding] bootstrap_projects_seeds failed:",
            projectsErr,
          );
        }
        if (supplementErr) {
          // eslint-disable-next-line no-console
          console.warn(
            "[onboarding] bootstrap_supplement_seeds failed:",
            supplementErr,
          );
        }
      }

      // Sync prefs store (units, theme already applied) and clear caches.
      setUnits(identity.units_preference);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["athlete_profile", userId] }),
        qc.invalidateQueries({ queryKey: ["upcoming_milestones", userId] }),
        qc.invalidateQueries({ queryKey: ["onboarding_status", userId] }),
        qc.invalidateQueries({ queryKey: ["onboarding_existing", userId] }),
        qc.invalidateQueries({ queryKey: ["active_context", userId] }),
      ]);
      navigate("/", { replace: true });
    } catch (e) {
      setError(formatError(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="onboarding-shell">
      <main className="onboarding-card">
        {hasExisting && (
          <div
            style={{
              marginBottom: "var(--space-6)",
              padding: "var(--space-3) var(--space-4)",
              borderRadius: "var(--radius-card)",
              background: "var(--accent-wash)",
              border: "1px solid var(--accent-dim)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "var(--space-3)",
              flexWrap: "wrap",
              fontSize: "var(--type-body-s)",
              color: "var(--fg)",
            }}
            role="status"
          >
            <span>You've already set up Haus. Editing will update your profile.</span>
            <button
              type="button"
              onClick={() => navigate("/", { replace: true })}
              style={{
                background: "transparent",
                color: "var(--fg)",
                border: "1px solid var(--hair-2)",
                borderRadius: "var(--radius-chip)",
                padding: "4px 12px",
                fontSize: "var(--type-meta)",
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              Skip → Today
            </button>
          </div>
        )}
        <header style={{ marginBottom: "var(--space-8)" }}>
          <p
            style={{
              fontSize: "var(--type-micro)",
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: "var(--fg-3)",
              margin: 0,
            }}
          >
            Step {step} of 3 · {hasExisting ? "Edit profile" : "Setup"}
          </p>
          <h1 className="onboarding-title">
            {step === 1 && "Tell Haus who you are."}
            {step === 2 && "Where you are, how you eat."}
            {step === 3 && "Looks right?"}
          </h1>
          <p
            style={{
              color: "var(--fg-3)",
              fontSize: "var(--type-body-s)",
              margin: "var(--space-2) 0 0",
              lineHeight: 1.5,
            }}
          >
            Signed in as {userEmail}. This takes about 90 seconds. You can edit
            anything later from Settings.
          </p>
        </header>

        {step === 1 && (
          <IdentityStep
            value={identity}
            onChange={setIdentity}
            onNext={() => setStep(2)}
          />
        )}
        {step === 2 && (
          <ContextStep
            value={ctx}
            onChange={setCtx}
            onBack={() => setStep(1)}
            onNext={() => setStep(3)}
          />
        )}
        {step === 3 && (
          <ConfirmStep
            identity={identity}
            ctx={ctx}
            submitting={submitting}
            error={error}
            isEdit={hasExisting}
            onBack={() => setStep(2)}
            onSubmit={submit}
          />
        )}
      </main>
    </div>
  );
}

// ---- Step 1: Identity --------------------------------------------------------
function IdentityStep({
  value,
  onChange,
  onNext,
}: {
  value: Identity;
  onChange: (v: Identity) => void;
  onNext: () => void;
}) {
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onNext();
      }}
      style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}
    >
      <Input
        label="Display name"
        value={value.display_name}
        onChange={(v) => onChange({ ...value, display_name: v })}
        placeholder="Jonas"
        required
        autoFocus
      />
      <Row>
        <Select
          label="Sex at birth"
          value={value.sex_at_birth}
          onChange={(v) =>
            onChange({ ...value, sex_at_birth: v as Identity["sex_at_birth"] })
          }
          options={[
            { value: "", label: "—" },
            { value: "male", label: "Male" },
            { value: "female", label: "Female" },
            { value: "intersex", label: "Intersex" },
          ]}
        />
        <Input
          label="Date of birth"
          type="date"
          value={value.date_of_birth}
          onChange={(v) => onChange({ ...value, date_of_birth: v })}
        />
      </Row>
      <Row>
        <Input
          label="Height (cm)"
          type="number"
          inputMode="decimal"
          value={value.height_cm}
          onChange={(v) => onChange({ ...value, height_cm: v })}
          placeholder="178"
        />
        <Input
          label="Current weight (kg)"
          type="number"
          inputMode="decimal"
          value={value.current_weight_kg}
          onChange={(v) => onChange({ ...value, current_weight_kg: v })}
          placeholder="64.5"
        />
        <Input
          label="Goal weight (kg)"
          type="number"
          inputMode="decimal"
          value={value.goal_weight_kg}
          onChange={(v) => onChange({ ...value, goal_weight_kg: v })}
          placeholder="63"
        />
      </Row>
      <Row>
        <Select
          label="Units"
          value={value.units_preference}
          onChange={(v) =>
            onChange({
              ...value,
              units_preference: v as UnitsPreference,
            })
          }
          options={[
            {
              value: "metric_with_imperial_secondary",
              label: "Metric · imperial secondary",
            },
            { value: "metric", label: "Metric only" },
            { value: "imperial", label: "Imperial only" },
          ]}
        />
        <Select
          label="Language"
          value={value.language}
          onChange={(v) => onChange({ ...value, language: v })}
          options={[
            { value: "en", label: "English" },
            { value: "de", label: "Deutsch" },
          ]}
        />
      </Row>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: "var(--space-3)", marginTop: "var(--space-2)" }}>
        <Primary type="submit">Continue</Primary>
      </div>
    </form>
  );
}

// ---- Step 2: Context ---------------------------------------------------------
function ContextStep({
  value,
  onChange,
  onBack,
  onNext,
}: {
  value: ContextValues;
  onChange: (v: ContextValues) => void;
  onBack: () => void;
  onNext: () => void;
}) {
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onNext();
      }}
      style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}
    >
      <Row>
        <Select
          label="Diet"
          value={value.diet}
          onChange={(v) => onChange({ ...value, diet: v as ContextValues["diet"] })}
          options={[
            { value: "vegan", label: "Vegan" },
            { value: "vegetarian", label: "Vegetarian" },
            { value: "pescatarian", label: "Pescatarian" },
            { value: "omnivore", label: "Omnivore" },
            { value: "custom", label: "Custom" },
          ]}
        />
        <Select
          label="Kitchen"
          value={value.kitchen}
          onChange={(v) => onChange({ ...value, kitchen: v as ContextValues["kitchen"] })}
          options={[
            { value: "full", label: "Full kitchen" },
            { value: "boil_only", label: "Boil only" },
            { value: "thaw_only", label: "Thaw only" },
            { value: "camp_stove", label: "Camp stove" },
            { value: "restaurant_only", label: "Restaurants only" },
            { value: "no_kitchen", label: "No kitchen" },
          ]}
        />
      </Row>
      <Row>
        <Select
          label="Activity mode"
          value={value.activity_mode}
          onChange={(v) => onChange({ ...value, activity_mode: v })}
          options={[
            { value: "home", label: "Home" },
            { value: "travel", label: "Travel" },
            { value: "bike_tour", label: "Bike tour" },
            { value: "running_camp", label: "Running camp" },
            { value: "race_day", label: "Race day" },
            { value: "race_week", label: "Race week" },
            { value: "conference", label: "Conference" },
            { value: "visiting_family", label: "Visiting family" },
            { value: "restaurant_outing", label: "Restaurant outing" },
            { value: "sick_day", label: "Sick day" },
          ]}
        />
        <Select
          label="Cooking for"
          value={value.cooking_for}
          onChange={(v) =>
            onChange({ ...value, cooking_for: v as ContextValues["cooking_for"] })
          }
          options={[
            { value: "self", label: "Self" },
            { value: "self_plus_partner", label: "Self + partner" },
            { value: "self_plus_guests", label: "Self + guests" },
            { value: "guests_only", label: "Guests only" },
          ]}
        />
      </Row>
      <Row>
        <Input
          label="Country (ISO 2)"
          value={value.country}
          onChange={(v) => onChange({ ...value, country: v.toUpperCase() })}
          placeholder="CH"
          maxLength={2}
        />
        <Input
          label="City"
          value={value.city}
          onChange={(v) => onChange({ ...value, city: v })}
          placeholder="Zürich"
        />
        <Input
          label="Timezone (IANA)"
          value={value.time_zone}
          onChange={(v) => onChange({ ...value, time_zone: v })}
          placeholder="Europe/Zurich"
        />
      </Row>

      <div style={{ display: "flex", justifyContent: "space-between", gap: "var(--space-3)", marginTop: "var(--space-2)" }}>
        <Secondary onClick={onBack}>Back</Secondary>
        <Primary type="submit">Continue</Primary>
      </div>
    </form>
  );
}

// ---- Step 3: Confirm ---------------------------------------------------------
function ConfirmStep({
  identity,
  ctx,
  submitting,
  error,
  isEdit,
  onBack,
  onSubmit,
}: {
  identity: Identity;
  ctx: ContextValues;
  submitting: boolean;
  error: string | null;
  isEdit: boolean;
  onBack: () => void;
  onSubmit: () => void;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
      <RecapList
        rows={[
          ["Name", identity.display_name || "—"],
          [
            "Body",
            [
              identity.sex_at_birth || "—",
              identity.date_of_birth || "—",
              identity.height_cm ? `${identity.height_cm} cm` : "—",
              identity.current_weight_kg ? `${identity.current_weight_kg} kg` : "—",
              identity.goal_weight_kg ? `goal ${identity.goal_weight_kg} kg` : "no goal",
            ].join(" · "),
          ],
          ["Units", identity.units_preference.replace(/_/g, " ")],
          ["Language", identity.language],
          ["Diet", ctx.diet],
          ["Kitchen", ctx.kitchen.replace(/_/g, " ")],
          ["Activity", ctx.activity_mode.replace(/_/g, " ")],
          ["Cooking for", ctx.cooking_for.replace(/_/g, " ")],
          ["Where", `${ctx.city}, ${ctx.country} (${ctx.time_zone})`],
        ]}
      />

      <p
        style={{
          fontSize: "var(--type-body-s)",
          color: "var(--fg-3)",
          margin: 0,
          lineHeight: 1.5,
        }}
      >
        {isEdit
          ? "Saving updates your profile and current Context. Existing data, seeds, and history stay put."
          : "Continuing here writes your AthleteProfile + initial Context, plus loads 24 Sport entries + 14 Evidence seeds."}
      </p>

      {error && (
        <p
          role="alert"
          style={{
            color: "var(--brick)",
            fontSize: "var(--type-body-s)",
            background: "rgba(199,107,107,0.1)",
            padding: "var(--space-3)",
            borderRadius: "var(--radius-card)",
            border: "1px solid var(--brick)",
            margin: 0,
          }}
        >
          {error}
        </p>
      )}

      <div style={{ display: "flex", justifyContent: "space-between", gap: "var(--space-3)" }}>
        <Secondary onClick={onBack} disabled={submitting}>
          Back
        </Secondary>
        <Primary onClick={onSubmit} disabled={submitting}>
          {submitting ? "Saving…" : isEdit ? "Save changes" : "Finish setup"}
        </Primary>
      </div>
    </div>
  );
}

// ---- Tiny form primitives ----------------------------------------------------
function Row({ children }: { children: React.ReactNode }) {
  const cols = Array.isArray(children) ? children.length : 1;
  return (
    <div
      className="onboarding-row"
      style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}
    >
      {children}
    </div>
  );
}

function Input({
  label,
  value,
  onChange,
  type = "text",
  placeholder,
  required,
  autoFocus,
  inputMode,
  maxLength,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  required?: boolean;
  autoFocus?: boolean;
  inputMode?: "text" | "decimal" | "numeric" | "email" | "tel" | "url" | "search";
  maxLength?: number;
}) {
  return (
    <label
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--space-2)",
        fontSize: "var(--type-body-s)",
        color: "var(--fg-2)",
      }}
    >
      {label}
      <input
        type={type}
        value={value}
        required={required}
        autoFocus={autoFocus}
        inputMode={inputMode}
        maxLength={maxLength}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        style={{
          height: 38,
          padding: "0 var(--space-3)",
          background: "var(--bg-3)",
          color: "var(--fg)",
          border: "1px solid var(--hair-2)",
          borderRadius: "var(--radius-card)",
          fontFamily: "var(--font-sans)",
          fontSize: "var(--type-body)",
        }}
      />
    </label>
  );
}

function Select({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <label
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--space-2)",
        fontSize: "var(--type-body-s)",
        color: "var(--fg-2)",
      }}
    >
      {label}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          height: 38,
          padding: "0 var(--space-3)",
          background: "var(--bg-3)",
          color: "var(--fg)",
          border: "1px solid var(--hair-2)",
          borderRadius: "var(--radius-card)",
          fontFamily: "var(--font-sans)",
          fontSize: "var(--type-body)",
        }}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function Primary({
  children,
  type = "button",
  onClick,
  disabled,
}: {
  children: React.ReactNode;
  type?: "button" | "submit";
  onClick?: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      style={{
        height: 40,
        padding: "0 var(--space-5)",
        background: "var(--accent)",
        color: "#0a0b0d",
        fontWeight: 600,
        fontSize: "var(--type-body)",
        borderRadius: "var(--radius-card)",
        border: "1px solid transparent",
        cursor: disabled ? "wait" : "pointer",
        opacity: disabled ? 0.6 : 1,
      }}
    >
      {children}
    </button>
  );
}

function Secondary({
  children,
  onClick,
  disabled,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        height: 40,
        padding: "0 var(--space-4)",
        background: "transparent",
        color: "var(--fg-2)",
        fontSize: "var(--type-body)",
        borderRadius: "var(--radius-card)",
        border: "1px solid var(--hair-2)",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {children}
    </button>
  );
}

function RecapList({ rows }: { rows: [string, string][] }) {
  return (
    <dl
      style={{
        margin: 0,
        display: "grid",
        gridTemplateColumns: "max-content 1fr",
        rowGap: "var(--space-2)",
        columnGap: "var(--space-4)",
        fontSize: "var(--type-body-s)",
      }}
    >
      {rows.map(([k, v]) => (
        <div key={k} style={{ display: "contents" }}>
          <dt
            style={{
              color: "var(--fg-3)",
              fontSize: "var(--type-micro)",
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              alignSelf: "center",
            }}
          >
            {k}
          </dt>
          <dd style={{ margin: 0, color: "var(--fg)" }}>{v}</dd>
        </div>
      ))}
    </dl>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "var(--space-6)",
      }}
    >
      {children}
    </div>
  );
}
